import { Dispatch } from 'redux';
import {
  addAircraftTotals,
  deleteTripEntry,
  deleteTripUpdate,
  getTrip,
  queryTrips,
  updateTripEntry,
  updateTripDetails,
  updateTripNumber,
  addTrip,
} from '../../services/api';
import { ToastCategories, ToastTypes, ToastsActionTypes } from '../toasts';
import { intl } from '../../providers/IntlProvider';
import handleNetworkCall from '../handleNetworkCall';
import { Trip, TripsActionTypes } from '.';

export const getAircraftTripEntries =
  ({ payload }: { payload: { id: string } }) =>
    async (dispatch: Dispatch): Promise<void> => {
    await handleNetworkCall({
      networkCall: () => queryTrips(payload),
      dispatch,
      successActionType: TripsActionTypes.SAVE_ALL,
      errorMessageId: 'text.failedToGetTrips',
      returnBody: false,
      formatMessage: intl.formatMessage,
    });
  };

export const getTripDetails =
  ({ payload }: { payload: string }) =>
    async (dispatch: Dispatch): Promise<void> => {
    await handleNetworkCall({
      networkCall: () => getTrip(payload),
      dispatch,
      successActionType: TripsActionTypes.UPDATE,
      errorMessageId: 'text.unexpectedResponse',
      returnBody: false,
      formatMessage: intl.formatMessage,
    });
  };

export const saveTripEntry =
  ({ payload }: { payload: { id: string; trip: Trip } }) =>
    async (dispatch: Dispatch): Promise<Trip> => {
    const response = await handleNetworkCall({
      networkCall: () => addTrip(payload),
      dispatch,
      successActionType: TripsActionTypes.SAVE,
      errorMessageId: 'text.failedToSaveTrip',
      returnBody: false,
      formatMessage: intl.formatMessage,
    });
    return response as Trip;
  };

export const storeEphemeralTrip =
  ({ payload }: { payload: Trip }) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: TripsActionTypes.SAVE_EPHEMERAL_ENTRY, payload });
  };

export const setTripsTableSource =
  ({ payload }: { payload: string }) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: TripsActionTypes.SAVE_SOURCE, payload });
  };

export const addUpdate =
  ({ payload }: { payload: { aircraftId: string; trip: Trip } }) =>
    async (dispatch: Dispatch): Promise<Trip> => {
    const responseBody = await handleNetworkCall({
      networkCall: () => addAircraftTotals(payload),
      dispatch,
      successActionType: TripsActionTypes.SAVE,
      errorMessageId: 'text.failedToUpdateTrip',
      formatMessage: intl.formatMessage,
    });
    return responseBody as Trip;
  };

export const updateEntry =
  ({ payload }: { payload: { id: string; trip: Trip } }) =>
    async (dispatch: Dispatch): Promise<void> => {
    await handleNetworkCall({
      networkCall: () => updateTripEntry(payload),
      dispatch,
      successActionType: TripsActionTypes.UPDATE,
      errorMessageId: 'text.failedToUpdateTripUpdate',
      formatMessage: intl.formatMessage,
    });
  };

export const updateTripFromDrawer =
  ({ payload }: { payload: { id: string; trip: Trip } }) =>
    async (dispatch: Dispatch): Promise<Trip> => {
    const responseBody = await handleNetworkCall({
      networkCall: () => updateTripDetails(payload),
      dispatch,
      successActionType: TripsActionTypes.UPDATE,
      errorMessageId: 'text.failedToUpdateTrip',
      formatMessage: intl.formatMessage,
    });
    return responseBody as Trip;
  };

export const updateTrip =
  ({ payload }: { payload: { id: string; trip: Trip, srp_number?: boolean } }) =>
    async (dispatch: Dispatch): Promise<void> => {
      await handleNetworkCall({
      networkCall: () => (payload.srp_number ? updateTripNumber(payload) : updateTripDetails(payload)),
      dispatch,
      successActionType: TripsActionTypes.UPDATE,
      errorMessageId: 'text.failedToUpdateTrip',
      formatMessage: intl.formatMessage,
    });
  };

export const deleteEntry =
  ({ payload }: { payload: { id: string; CRSNameOverride?: string } }, callBack = null) =>
  async (dispatch: Dispatch): Promise<void> => {
    // @TODO determine how these influence the tripsMap and implement reducers for them.
    const response = await deleteTripEntry(payload);

    const handleError = (messageId: string, field?: string) => {
      dispatch({
        type: ToastsActionTypes.ADD_TOAST,
        payload: {
          title: intl.formatMessage({ id: messageId }),
          type: ToastTypes.ERROR,
          category: ToastCategories.FLAG,
          message: field ? intl.formatMessage({ id: 'text.tripCouldNotBeDeleted' }, { field }) : '',
          },
        });
      if (callBack) callBack({ failed: true });
    };

    if (response && response.status > 199 && response.status < 400) {
      if (callBack) {
        callBack({ failed: false });
      }
      dispatch({
        type: TripsActionTypes.DELETE_TRIP,
        payload,
      });
      dispatch({
        type: ToastsActionTypes.ADD_TOAST,
        payload: {
          title: intl.formatMessage({ id: 'message.tripRemoved' }),
          type: ToastTypes.SUCCESS,
          category: ToastCategories.FLASH,
          message: '',
          },
        });
    } else if (response && response.status === 422) {
      const errorData = await response.json();
      const errorMessage = errorData?.message;
      if (errorMessage?.includes('Cannot delete record because of dependent')) {
        handleError('text.tripDeletionFailed', payload.CRSNameOverride);
      } else {
        handleError('message.tripRemovalFailed');
      }
    } else {
      handleError('message.tripRemovalFailed');
    }
  };

export const deleteUpdate =
  ({ payload }: { payload: { id: string; CRSNameOverride?: string } }, callBack = null) =>
  async (dispatch: Dispatch): Promise<void> => {
    // @TODO determine how these influence the tripsMap and implement reducers for them.
    const response = await deleteTripUpdate(payload);

    const handleError = (messageId: string, field?: string) => {
      dispatch({
        type: ToastsActionTypes.ADD_TOAST,
        payload: {
          title: intl.formatMessage({ id: messageId }),
          type: ToastTypes.ERROR,
          category: ToastCategories.FLAG,
          message: field ? intl.formatMessage({ id: 'text.tripUpdateCouldNotBeDeleted' }, { field }) : '',
          },
        });
      if (callBack) callBack({ failed: true });
    };

    if (response && response.status > 199 && response.status < 400) {
      if (callBack) {
        callBack({ failed: false });
      }
      dispatch({
        type: TripsActionTypes.DELETE_TRIP,
        payload,
      });

      dispatch({
        type: ToastsActionTypes.ADD_TOAST,
        payload: {
          title: intl.formatMessage({ id: 'message.tripUpdateRemoved' }),
          type: ToastTypes.SUCCESS,
          category: ToastCategories.FLASH,
          message: '',
        },
      });
    } else if (response && response.status === 422) {
      const errorData = await response.json();
      const errorMessage = errorData?.message;
      if (errorMessage?.includes('Cannot delete record because of dependent')) {
        handleError('text.tripUpdateDeletionFailed', payload?.CRSNameOverride);
      } else {
        handleError('message.tripUpdateRemovalFailed');
      }
    } else {
      handleError('message.tripUpdateRemovalFailed');
    }
  };
